/**
 * The safety net under every save, in IndexedDB. Ported from the desktop's
 * `main/recovery.ts` and `main/journal-owners.ts`.
 *
 *   versions - a file's previous bytes, kept just before a save replaces them.
 *              Covers "the save itself wrote something wrong", which for a
 *              WYSIWYG editor re-serializing markdown is a real possibility.
 *
 *   journal  - unsaved text, written on a debounce while editing. Covers "the
 *              tab died before the user saved": a crash, a closed laptop, the
 *              browser killing a background tab.
 *
 * Each tab is an owner, as each window was on the desktop: a journal is offered
 * only to the tab that wrote it, or when that tab is gone, so a second tab is
 * never offered the first one's live work as "recovered". A tab's identity
 * lives in sessionStorage, which survives a reload of that tab and nothing
 * else, and it holds a Web Lock for as long as it is alive.
 */
import { db, randomId, type JournalRecord, type VersionRecord } from './db'
import type { BackupInfo, JournalEntry, VersionInfo } from './types'
import { decodeText } from './textfile'

// --- tab identity -------------------------------------------------------------

const TAB_KEY = 'ekram-md:tab'
const LOCK_PREFIX = 'ekram-md:tab:'

let tabId = ''
let tabReady: Promise<void> | null = null

function hasLocks(): boolean {
  return typeof navigator !== 'undefined' && !!navigator.locks
}

/**
 * Claims an identity for this tab. A duplicated tab inherits its parent's
 * sessionStorage, and with it the parent's id, so an id whose lock is already
 * held by a live tab is given up for a new one.
 */
export function claimTab(): Promise<void> {
  if (tabReady) return tabReady
  tabReady = (async () => {
    let id = ''
    try {
      id = sessionStorage.getItem(TAB_KEY) ?? ''
    } catch {
      /* storage blocked: a fresh id every load */
    }
    if (!hasLocks()) {
      tabId = id || randomId()
      return
    }
    for (let attempt = 0; attempt < 3; attempt++) {
      const candidate = id || randomId()
      const got = await new Promise<boolean>((resolve) => {
        void navigator.locks.request(LOCK_PREFIX + candidate, { ifAvailable: true }, (lock) => {
          resolve(lock !== null)
          // Held for the page's life: the lock's promise never settles.
          return lock ? new Promise<void>(() => {}) : undefined
        })
      })
      if (got) {
        tabId = candidate
        break
      }
      id = ''
    }
    if (!tabId) tabId = randomId()
    try {
      sessionStorage.setItem(TAB_KEY, tabId)
    } catch {
      /* ignore */
    }
  })()
  return tabReady
}

export function currentTab(): string {
  return tabId
}

/** The ids of every tab of the app alive now, this one included. */
export async function liveTabs(): Promise<Set<string>> {
  if (!hasLocks()) return new Set([tabId])
  const { held = [] } = await navigator.locks.query()
  return new Set(
    held
      .map((l) => l.name ?? '')
      .filter((n) => n.startsWith(LOCK_PREFIX))
      .map((n) => n.slice(LOCK_PREFIX.length))
  )
}

// --- journal ----------------------------------------------------------------

/** Debounced so typing does not hammer storage. */
const JOURNAL_AFTER_MS = 1500
const pendingJournal = new Map<string, { timer: number; entry: JournalRecord }>()

function keyFor(path: string): string {
  return path.toLowerCase()
}

async function writeJournal(entry: JournalRecord): Promise<void> {
  try {
    await (await db()).put('journal', { ...entry, path: entry.path })
  } catch {
    // A journal that could not be written is a lost safety net, not an error
    // to put in front of someone typing.
  }
}

/** Fire and forget: journalling must never block a keystroke. */
export function journal(path: string, content: string): void {
  const key = keyFor(path)
  const existing = pendingJournal.get(key)
  if (existing) clearTimeout(existing.timer)
  const entry: JournalRecord = { path, content, at: Date.now(), owner: tabId }
  const timer = window.setTimeout(() => {
    pendingJournal.delete(key)
    void writeJournal(entry)
  }, JOURNAL_AFTER_MS)
  pendingJournal.set(key, { timer, entry })
}

/**
 * Writes debounced entries at once, as the page goes into the background: a
 * hidden tab may be discarded without another chance.
 */
export function flushJournals(): void {
  for (const [, p] of pendingJournal) {
    clearTimeout(p.timer)
    void writeJournal(p.entry)
  }
  pendingJournal.clear()
}

/**
 * Writes debounced entries and waits until they are stored, for a reload the
 * page is about to make on purpose, where the write must not be left to chance.
 */
export async function flushJournalsAndWait(): Promise<void> {
  const writes: Array<Promise<void>> = []
  for (const [, p] of pendingJournal) {
    clearTimeout(p.timer)
    writes.push(writeJournal(p.entry))
  }
  pendingJournal.clear()
  await Promise.all(writes)
}

export async function clearJournal(path: string): Promise<void> {
  const key = keyFor(path)
  const p = pendingJournal.get(key)
  if (p) {
    clearTimeout(p.timer)
    pendingJournal.delete(key)
  }
  const d = await db()
  // Stored under the path as written; an earlier spelling may differ in case.
  for (const k of await d.getAllKeys('journal')) {
    if (keyFor(k) === key) await d.delete('journal', k)
  }
}

/**
 * Journals this tab may be offered: its own, or those of tabs that are gone.
 * `matchesDisk` says whether the file already holds a journal's text, in which
 * case the journal is dropped rather than offered. It answers null when the
 * file cannot be read now (permission not yet given), and the journal is kept.
 */
export async function pendingRecoveries(
  matchesDisk: (entry: JournalEntry) => Promise<boolean | null>
): Promise<JournalEntry[]> {
  const live = await liveTabs()
  const d = await db()
  const out: JournalEntry[] = []
  for (const rec of await d.getAll('journal')) {
    if (rec.owner && rec.owner !== tabId && live.has(rec.owner)) continue
    if (typeof rec.content !== 'string' || !rec.path) {
      await d.delete('journal', rec.path)
      continue
    }
    // Discarded only when the file already holds it: a spurious offer costs a
    // dialog, a wrong discard costs the work.
    if ((await matchesDisk(rec).catch(() => null)) === true) {
      await d.delete('journal', rec.path)
      continue
    }
    out.push({ path: rec.path, content: rec.content, at: rec.at })
  }
  return out
}

// --- versions ---------------------------------------------------------------

const MAX_VERSIONS = 20
const MAX_BYTES = 10 * 1024 * 1024
const VERSION_ID = /^\d{13}-[0-9a-f]{6}$/

async function versionRecords(path: string): Promise<VersionRecord[]> {
  const all = await (await db()).getAllFromIndex('versions', 'byPath', keyFor(path))
  return all.sort((a, b) => b.id.localeCompare(a.id))
}

/** The versions kept for a path, newest first. */
export async function listVersions(path: string): Promise<VersionInfo[]> {
  return (await versionRecords(path)).map((v) => ({
    id: v.id,
    savedAtMs: v.savedAtMs,
    size: v.size,
  }))
}

export async function readVersion(path: string, id: string): Promise<Uint8Array | null> {
  if (typeof id !== 'string' || !VERSION_ID.test(id)) return null
  const rec = await (await db()).get('versions', `${keyFor(path)}|${id}`)
  return rec?.bytes ?? null
}

function sameBytes(a: Uint8Array, b: Uint8Array): boolean {
  if (a.length !== b.length) return false
  for (let i = 0; i < a.length; i++) if (a[i] !== b[i]) return false
  return true
}

/** Drops the oldest versions past the count and the size allowed; the newest always stays. */
async function prune(path: string): Promise<void> {
  const d = await db()
  let bytes = 0
  for (const [i, v] of (await versionRecords(path)).entries()) {
    bytes += v.size
    if (i > 0 && (i >= MAX_VERSIONS || bytes > MAX_BYTES)) await d.delete('versions', v.key)
  }
}

/**
 * Keeps a file's bytes before they are overwritten: raw bytes, because a copy
 * that has been through decoding is not the file that was replaced. A save that
 * changed nothing since the last version adds none.
 */
export async function backup(path: string, previous: Uint8Array): Promise<void> {
  try {
    const [latest] = await versionRecords(path)
    if (latest && sameBytes(latest.bytes, previous)) return
    // Later than the latest by name, even if the clock went back.
    const now = Math.max(Date.now(), latest ? latest.savedAtMs + 1 : 0)
    const id = `${String(now).padStart(13, '0')}-${randomId(3)}`
    const pathKey = keyFor(path)
    await (
      await db()
    ).put('versions', {
      key: `${pathKey}|${id}`,
      pathKey,
      path,
      id,
      savedAtMs: now,
      size: previous.byteLength,
      bytes: previous,
    })
    await prune(path)
  } catch {
    // A backup that failed must not stop the save that asked for it.
  }
}

/** Takes versions along when a file, or every file under a folder, moves. */
export async function moveHistoryUnder(from: string, to: string): Promise<void> {
  const lower = keyFor(from).replace(/\/+$/, '')
  const d = await db()
  const moved = new Set<string>()
  for (const v of await d.getAll('versions')) {
    let next: string | null = null
    if (v.pathKey === lower) next = to
    else if (v.pathKey.startsWith(`${lower}/`)) next = to + v.path.slice(lower.length)
    if (next === null) continue
    await d.delete('versions', v.key)
    const pathKey = keyFor(next)
    await d.put('versions', { ...v, key: `${pathKey}|${v.id}`, pathKey, path: next })
    moved.add(next)
  }
  for (const p of moved) await prune(p)
}

export async function backupInfo(path: string): Promise<BackupInfo> {
  const [latest] = await versionRecords(path)
  if (!latest) return { exists: false }
  return {
    exists: true,
    content: decodeText(latest.bytes).content,
    savedAtMs: latest.savedAtMs,
    size: latest.size,
  }
}
